import { View, StyleSheet, Text } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { useUpload } from "@/models/useUpload";
import { useEffect } from "react";
import { Colors } from "@/constants";
import { logFunnelEvent } from "@/fetchData/datadog";

const AlertWidgetStartUpload = () => {
  const params = useUISlice((state) => state.alertWidgetStartUpload);
  const setVisible = useUISlice((state) => state.setAlertWidgetStartUpload);
  const { uploadFile, uploadProgress, setUploadParams } = useUpload({
    maxFileSize: 5 * 1024 * 1024 * 1024, // 5GB
  });
  const width = useUISlice((state) => state.device.width);
  const { t } = useTranslation();

  useEffect(() => {
    const getUploadedFileUrl = async (): Promise<string | undefined> => {
      if (!params) return;

      const fileExtension = params?.file?.name?.split(".").pop() || "";
      setUploadParams({ fileType: params?.file?.type, fileExtension });

      logFunnelEvent("[2] - upload started");

      const uploadResponse = await uploadFile(params?.file);

      if (!uploadResponse.success) return;

      logFunnelEvent("[2.2] - upload completed");

      return uploadResponse.downloadUrl;
    };

    getUploadedFileUrl().then((url) => {
      if (url) {
        params?.onSuccess?.({ downloadUrl: url, language: params.language });
        setVisible(undefined);
      }
    });
  }, [params]);

  if (!params) return null;

  return (
    <ModalContainer
      shouldPersistBackdropClick
      hideDismissIcon
      title={t("alertWidgetStartUpload.title")}
      subtitle={t("alertWidgetStartUpload.subtitle")}
      onDismiss={() => null}
      hideCancel
      modalWidth={Math.min(width, 700)}
    >
      <View style={styles.spacer} />

      <View style={styles.progressBarContainer}>
        <Text
          style={{
            position: "absolute",
            left: ms(16),
            top: "33%",
            color: Colors.text,
            fontWeight: "500",
            fontSize: ms(18),
            zIndex: 10,
          }}
        >
          {params.file.name}
        </Text>
        <View
          style={[
            styles.progressBar,
            { width: `${uploadProgress}%`, backgroundColor: Colors.accent },
          ]}
        />
      </View>

      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(32),
  },
  negativeMargin: {
    marginBottom: -ms(24),
  },
  progressBarContainer: {
    width: "100%",
    height: ms(64),
    backgroundColor: Colors.gray3,
    borderRadius: ms(16),
    marginBottom: ms(12),
  },
  progressBar: {
    height: "100%",
    borderRadius: ms(16),
  },
});

export default AlertWidgetStartUpload;
